import { useMemo } from "react";
import { useTexture } from "@react-three/drei";
import { SRGBColorSpace, RepeatWrapping } from "three";
import { imgUrl } from "@/lib/api";
import { finishBg } from "@/components/FinishDots";

// 1x1 transparent pixel so useTexture always receives a URL (hooks can't be conditional)
const BLANK = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

export const PLAIN_BOARD = "#8d8578";
export const PLAIN_STONE = "#d9d6cf";

/** Loads the board and stone product photos as repeating, colour-managed textures. */
export const useSurfaceTextures = (board, stone) => {
  const [boardTex, stoneTex] = useTexture([board ? imgUrl(board.images?.[0]) : BLANK, stone ? imgUrl(stone.images?.[0]) : BLANK]);
  return useMemo(() => {
    for (const t of [boardTex, stoneTex]) {
      t.colorSpace = SRGBColorSpace;
      t.wrapS = t.wrapT = RepeatWrapping;
      t.anisotropy = 16;
      t.needsUpdate = true;
    }
    return { boardTex: board ? boardTex : null, stoneTex: stone ? stoneTex : null };
  }, [boardTex, stoneTex, board, stone]);
};

/** A clone of a texture mapped so the grain runs continuously across neighbouring panels. tile = real-world size (m) of one photo. */
export const useMappedTexture = (tex, x, y, w, h, tile) =>
  useMemo(() => {
    if (!tex) return null;
    const t = tex.clone();
    t.repeat.set(w / tile, h / tile);
    t.offset.set(((x % tile) + tile) % tile / tile, ((y % tile) + tile) % tile / tile);
    t.needsUpdate = true;
    return t;
  }, [tex, x, y, w, h, tile]);

/** Metallic colour + roughness for a handle finish name, e.g. "Brushed Brass". */
export const finishMaterial = (finish) => {
  const hexes = (finish && finishBg(finish)?.match(/#[0-9a-f]{6}/gi)) || [];
  return { color: hexes[2] || hexes[0] || "#c9a24a", roughness: /brushed|antique|matt/i.test(finish || "") ? 0.45 : 0.18, metalness: 0.95 };
};

/** Classifies a hardware item's style string into a 3D handle type. */
export const handleKind = (style = "") => {
  const k = style.toLowerCase();
  if (k.includes("knob")) return "knob";
  if (k.includes("cup")) return "cup";
  if (/profile|lip|recess|edge/.test(k)) return "profile";
  return "bar";
};
